/**
 * Mixes two arbitrary colors together in the given ratio, using the native CSS `color-mix()` function.
 *
 * Unlike MUI's `lighten()`/`darken()` utilities — which only blend a color towards pure white or pure
 * black respectively — this function blends between two actual colors you provide (e.g. two adjacent
 * shades from the same palette, like `red[50]` and `red[100]`). This makes it the right tool when you
 * need an intermediate step within an existing color scale, rather than a generic "lighter"/"darker"
 * variant of a single color.
 *
 * @param colorA - first color (any valid CSS color: hex, rgb, palette token, CSS variable, etc.)
 * @param colorB - second color
 * @param ratio - how far to move from colorA towards colorB, from 0 to 1 (default 0.5 — an even 50/50 mix).
 *   0 returns colorA, 1 returns colorB.
 * @returns a `color-mix()` CSS string, safe to use anywhere a color value is expected (sx, styled, inline style)
 *
 * @example
 * // mixing shade 200 and shade 300 with the default ratio gives you an in-between "shade 250"
 * mixColors(theme.palette.red[200], theme.palette.red[300])
 *
 * // mixing shade 300 and shade 400 with ratio 0.75 gives you "shade 375" (75% of the way to 400)
 * mixColors(theme.palette.red[300], theme.palette.red[400], 0.75)
 */
export const mixColors = (colorA, colorB, ratio = 0.5) => {
    return `color-mix(in srgb, ${colorA} ${(1 - ratio) * 100}%, ${colorB} ${ratio * 100}%)`;
};
